import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  Search,
  X,
} from "lucide-react";
import Header from "../../components/Header";
import LoadingLogo from "../../components/LoadingLogo";
import {
  getAuthenticatedAxios,
  buildApiPath,
  API_ENDPOINTS,
} from "../../utils/api";

const DEFAULT_DAYS = 180;
const MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", { month: "short" });
const METRIC_TOOLTIPS = {
  "Last active": "最近一次有检索行为的日期。用来判断这个用户最后一次实际使用系统是什么时候。",
  "Active days": "在当前统计周期内，至少有一次检索行为的天数。格式为活跃天数/统计总天数。",
  Searches: "在当前统计周期内，这个用户发起的检索总次数。",
  "Avg / active day": "只按活跃日计算的平均检索次数。可以看出用户在真正使用当天的检索密度。",
  "Papers seen": "用户实际看到过的论文数量。论文卡片或表格行进入可视区域并停留约 1 秒后记为 seen。",
  "Title clicks": "用户点击论文标题链接的次数，反映用户愿不愿意进一步打开论文详情。",
  "Full-text clicks": "用户点击全文按钮的次数，说明用户对结果有更强的深入阅读意图。",
  Bookmarks: "用户添加书签的次数，通常表示这篇论文值得后续回看或保留。",
  "CSV exports": "用户导出 CSV 的次数，说明这次检索结果已经进入后续整理或业务使用流程。",
  "History opens": "用户从历史记录重新打开检索结果的次数，表示用户有回看历史结果的意图。",
  "Sidebar opens": "用户从左侧历史侧边栏重新打开检索结果的次数。",
  "Detail opens": "用户从历史详情页打开检索结果的次数。",
  "Last history open": "最近一次重新打开历史检索结果的时间。",
  "Title CTR": "标题点击率 = Title clicks / Papers seen。表示用户看到结果后，点开标题的比例。",
  "Full-text CTR": "全文点击率 = Full-text clicks / Papers seen。表示用户看到结果后，继续打开全文的比例。",
  "Bookmark rate": "书签率 = Bookmarks / Papers seen。表示用户看到结果后，愿意保留结果的比例。",
  Seen: "该条 query 下，用户实际看到过的论文数量。",
  "Full text": "该条 query 下，用户点击全文按钮的次数。",
  History: "该条 query 下，用户重新打开历史检索结果的次数。",
  CSV: "该条 query 下，用户导出 CSV 的次数。",
  Rank: "论文在结果列表中的排序位置，通常越靠前表示系统认为越相关。",
  Paper: "用户在该次检索中看到或点击过的论文标题。",
  Title: "该论文标题被点击的次数。",
  Bookmark: "该论文当前是否仍在用户的 bookmark 列表中。这里显示的是最终状态，不是 bookmark 事件次数。",
};

function UserActivityTimeline() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [period, setPeriod] = useState(null);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState(null);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [queryPage, setQueryPage] = useState(1);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [allUsersSelected, setAllUsersSelected] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const loadActivity = useCallback(
    async (page = currentPage, itemsPerPage = perPage) => {
      try {
        setError(null);
        setLoading(true);
        const axios = getAuthenticatedAxios();
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: itemsPerPage.toString(),
          days: DEFAULT_DAYS.toString(),
        });
        if (searchTerm) params.append("search", searchTerm);
        if (filterCompany) params.append("company_id", filterCompany);
        if (filterRole) params.append("role", filterRole);

        const [activityResponse, companiesResponse] = await Promise.all([
          axios.get(
            `${buildApiPath(API_ENDPOINTS.ADMIN.USER_ACTIVITY_SUMMARY)}?${params}`,
          ),
          axios.get(buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES)),
        ]);

        setItems(Array.isArray(activityResponse.data.items) ? activityResponse.data.items : []);
        setPeriod(activityResponse.data.period || null);
        setMeta(activityResponse.data.meta || {});
        setCurrentPage(activityResponse.data.meta?.page || page);
        setPerPage(activityResponse.data.meta?.per_page || itemsPerPage);
        setCompanies(companiesResponse.data.items || []);
      } catch (err) {
        setError("Failed to load user activity timeline");
      } finally {
        setLoading(false);
      }
    },
    [
      currentPage,
      filterCompany,
      filterRole,
      perPage,
      searchTerm,
    ],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCompany, filterRole, searchTerm]);

  useEffect(() => {
    loadActivity(currentPage, perPage);
  }, [currentPage, perPage, loadActivity]);

  const openTimeline = async (userId, page = 1) => {
    try {
      setError(null);
      setDrawerLoading(true);
      setQueryPage(page);
      const axios = getAuthenticatedAxios();
      const params = new URLSearchParams({
        days: DEFAULT_DAYS.toString(),
        query_page: page.toString(),
        query_per_page: "20",
      });
      const response = await axios.get(
        `${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/${userId}/activity?${params}`,
      );
      setSelectedTimeline(response.data);
    } catch (err) {
      setError("Failed to load selected user activity");
    } finally {
      setDrawerLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setAllUsersSelected(false);
    setSelectedUserIds((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleAllUsersSelection = () => {
    setAllUsersSelected((previous) => {
      const next = !previous;
      if (next) {
        setSelectedUserIds(new Set());
      }
      return next;
    });
  };

  const exportSelectedUserQueries = async () => {
    const userIds = Array.from(selectedUserIds);
    if (!allUsersSelected && userIds.length === 0) {
      setError("Select at least one user to export");
      return;
    }

    try {
      setError(null);
      setExporting(true);
      const axios = getAuthenticatedAxios();
      const response = await axios.post(
        `${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/query-export`,
        allUsersSelected ? { export_all: true } : { user_ids: userIds },
        { responseType: "blob" },
      );
      const filename = resolveDownloadFilename(
        response.headers,
        allUsersSelected
          ? "all_users_query_export.zip"
          : "selected_users_query_export.zip",
      );
      downloadBlob(response.data, filename, response.headers?.["content-type"]);
    } catch (err) {
      setError("Failed to export selected user queries");
    } finally {
      setExporting(false);
    }
  };

  const handleRowKeyDown = (event, userId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openTimeline(userId, 1);
    }
  };

  const totalUsers = meta.total || 0;
  const totalPages = meta.total_pages || 0;
  const exportButtonLabel = allUsersSelected
    ? "Export all users"
    : `Export selected (${selectedUserIds.size})`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <CalendarDays className="h-8 w-8 text-blue-700" />
              <h1 className="text-3xl font-bold text-gray-900">
                User Activity Timeline
              </h1>
            </div>
            <p className="text-gray-600 mt-2">
              Search activity by user over the last 6 months
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/admin/users")}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              User Management
            </button>
            <button
              onClick={() => navigate("/admin")}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <select
              value={filterCompany}
              onChange={(event) => setFilterCompany(event.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Companies</option>
              {companies.map((company, index) => {
                const companyId = company.id || company.CompanyId;
                const companyName = company.name || company.Name;
                return (
                  <option key={`${companyId || "company"}-${index}`} value={companyId}>
                    {companyName}
                  </option>
                );
              })}
            </select>

            <select
              value={filterRole}
              onChange={(event) => setFilterRole(event.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
              <option value="demo">Demo</option>
            </select>

          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Activity Heatmap
              </h2>
              <p className="text-sm text-gray-500">
                {period
                  ? `${period.start_date} to ${period.end_date} (${period.timezone})`
                : "Last 6 months"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={allUsersSelected}
                  onChange={toggleAllUsersSelection}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                All users
              </label>
              <button
                type="button"
                onClick={exportSelectedUserQueries}
                disabled={(!allUsersSelected && selectedUserIds.size === 0) || exporting}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <Download className="h-4 w-4" />
                {exporting ? "Exporting..." : exportButtonLabel}
              </button>
              <HeatmapLegend />
            </div>
          </div>
          <div className="px-6 py-3 bg-sky-50 border-b border-sky-100 text-sm text-sky-900 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
            <span className="font-medium">Showing the last 6 months of activity</span>
            <span>Each square represents one day. Hover to see the exact date.</span>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingLogo size="md" message="Loading activity..." />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No users found matching your filters
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {items.map((item) => (
                <div
                  key={item.user_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openTimeline(item.user_id, 1)}
                  onKeyDown={(event) => handleRowKeyDown(event, item.user_id)}
                  className="w-full text-left px-6 py-5 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
                >
                  <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-4">
                    <input
                      type="checkbox"
                      checked={allUsersSelected || selectedUserIds.has(item.user_id)}
                      disabled={allUsersSelected}
                      onChange={() => toggleUserSelection(item.user_id)}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      aria-label={`Select ${item.full_name || item.email}`}
                    />
                    <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] gap-5 items-start">
                      <UserActivitySummary item={item} />
                      <YearHeatmap activity={item.daily_activity || []} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && totalUsers > 0 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * perPage + 1, totalUsers)}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * perPage, totalUsers)}
                </span>{" "}
                of <span className="font-medium">{totalUsers}</span> users
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedTimeline && (
        <TimelineDrawer
          timeline={selectedTimeline}
          loading={drawerLoading}
          queryPage={queryPage}
          onClose={() => setSelectedTimeline(null)}
          onPageChange={(page) => openTimeline(selectedTimeline.user.id, page)}
        />
      )}
    </div>
  );
}

function UserActivitySummary({ item }) {
  const summary = item.summary || {};
  return (
    <div className="min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">
            {item.full_name || item.email}
          </p>
          <p className="text-sm text-gray-500 truncate">{item.email}</p>
          <p className="text-sm text-gray-500 truncate">
            {item.company_name || "No company"}
          </p>
        </div>
        <span className="px-2 py-0.5 rounded bg-gray-100 text-xs font-medium text-gray-700">
          {item.role}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <Metric label="Last active" value={summary.last_active_date || "-"} />
        <Metric
          label="Active days"
          value={`${summary.active_days || 0}/${summary.period_days || DEFAULT_DAYS}`}
        />
        <Metric label="Searches" value={summary.total_searches || 0} />
        <Metric
          label="Avg / active day"
          value={summary.avg_searches_per_active_day || 0}
        />
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <MetricLabel label={label} tone="gray" />
      <p className="mt-1 font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function YearHeatmap({ activity }) {
  const weeks = buildHeatmapWeeks(activity);
  const monthLabels = buildMonthLabels(weeks);

  if (weeks.length === 0) {
    return null;
  }

  return (
    <div className="w-full min-w-0">
      <div
        className="grid gap-1 mb-2 text-[11px] leading-none text-gray-400"
        style={{
          gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))`,
        }}
      >
        {monthLabels.map((label, index) => (
          <span key={`month-${index}`} className="truncate">
            {label}
          </span>
        ))}
      </div>
      <div
        className="grid gap-1"
        style={{
          gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))`,
        }}
        aria-label="Daily search activity heatmap"
      >
        {weeks.map((week, weekIndex) => (
          <div key={`week-${weekIndex}`} className="grid grid-rows-7 gap-1">
            {week.map((cell) =>
              cell.blank ? (
                <span
                  key={cell.key}
                  className="w-full aspect-square rounded-sm"
                />
              ) : (
                <span
                  key={cell.key}
                  title={`${cell.date}: ${cell.search_count} searches`}
                  className={`w-full aspect-square rounded-sm ${heatmapClass(cell.search_count)}`}
                />
              ),
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
        <span>Older</span>
        <span>Newer</span>
      </div>
    </div>
  );
}

function HeatmapLegend() {
  return (
    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
      <span>Less</span>
      {[0, 1, 3, 5].map((count) => (
        <span
          key={count}
          className={`h-3 w-3 rounded-sm ${heatmapClass(count)}`}
        />
      ))}
        <span>More</span>
    </div>
  );
}

function heatmapClass(count) {
  if (!count) return "bg-gray-100 border border-gray-200";
  if (count === 1) return "bg-cyan-200";
  if (count <= 4) return "bg-sky-400";
  return "bg-blue-700";
}

function buildHeatmapWeeks(activity) {
  if (!activity.length) {
    return [];
  }

  const leadingBlanks = new Date(`${activity[0].date}T00:00:00`).getDay();
  const cells = [
    ...Array.from({ length: leadingBlanks }, (_, index) => ({
      key: `blank-${index}`,
      blank: true,
    })),
    ...activity.map((day) => ({ ...day, key: day.date })),
  ];

  const weeks = [];
  for (let index = 0; index < cells.length; index += 7) {
    const week = cells.slice(index, index + 7);
    while (week.length < 7) {
      week.push({
        key: `blank-tail-${index + week.length}`,
        blank: true,
      });
    }
    weeks.push(week);
  }
  return weeks;
}

function buildMonthLabels(weeks) {
  let previousMonth = "";
  return weeks.map((week, weekIndex) => {
    const firstDay = week.find((cell) => !cell.blank && cell.date);
    if (!firstDay) {
      return "";
    }
    const monthLabel = MONTH_FORMATTER.format(new Date(`${firstDay.date}T00:00:00`));
    const dayOfMonth = Number(firstDay.date.split("-")[2]);
    if (weekIndex === 0 || (dayOfMonth <= 7 && monthLabel !== previousMonth)) {
      previousMonth = monthLabel;
      return monthLabel;
    }
    return "";
  });
}

function TimelineDrawer({
  timeline,
  loading,
  queryPage,
  onClose,
  onPageChange,
}) {
  const summary = timeline.summary || {};
  const queryMeta = timeline.query_meta || {};
  const engagement = timeline.result_engagement || {};
  const engagementSummary = engagement.summary || {};

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-900 bg-opacity-40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-xl flex flex-col">
        <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              User Activity Timeline
            </h2>
            <p className="text-gray-600 mt-1">
              {timeline.user.full_name || timeline.user.email}
            </p>
            <p className="text-sm text-gray-500">
              {timeline.user.email} · {timeline.user.company_name || "No company"}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Showing the last 6 months. Each square represents one day. Hover to see the exact date.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
            aria-label="Close timeline"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingLogo size="md" message="Loading timeline..." />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <DrawerMetric label="Last active" value={summary.last_active_date || "-"} />
                <DrawerMetric
                  label="Active days"
                  value={`${summary.active_days || 0}/${summary.period_days || DEFAULT_DAYS}`}
                />
                <DrawerMetric label="Searches" value={summary.total_searches || 0} />
                <DrawerMetric
                  label="Avg / active day"
                  value={summary.avg_searches_per_active_day || 0}
                />
              </div>

              <section className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Result Engagement
                    </h3>
                    <p className="text-sm text-gray-500">
                      Quality signals from papers seen, clicked, bookmarked, and exported.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <DrawerMetric label="Papers seen" value={engagementSummary.papers_seen || 0} />
                  <DrawerMetric label="Title clicks" value={engagementSummary.title_clicks || 0} />
                  <DrawerMetric label="Full-text clicks" value={engagementSummary.full_text_clicks || 0} />
                  <DrawerMetric label="Bookmarks" value={engagementSummary.bookmark_adds || 0} />
                  <DrawerMetric label="CSV exports" value={engagementSummary.csv_exports || 0} />
                  <DrawerMetric label="History opens" value={engagementSummary.history_opens || 0} />
                  <DrawerMetric label="Sidebar opens" value={engagementSummary.sidebar_history_opens || 0} />
                  <DrawerMetric label="Detail opens" value={engagementSummary.detail_history_opens || 0} />
                  <DrawerMetric
                    label="Last history open"
                    value={
                      engagementSummary.last_history_open_at
                        ? formatDateTime(engagementSummary.last_history_open_at)
                        : "-"
                    }
                  />
                  <DrawerMetric label="Title CTR" value={formatPercent(engagementSummary.title_ctr)} />
                  <DrawerMetric label="Full-text CTR" value={formatPercent(engagementSummary.full_text_ctr)} />
                  <DrawerMetric label="Bookmark rate" value={formatPercent(engagementSummary.bookmark_rate)} />
                </div>
              </section>

              <section className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Activity Heatmap
                  </h3>
                  <HeatmapLegend />
                </div>
                <div className="border border-gray-200 rounded-lg p-4 overflow-x-auto">
                  <YearHeatmap activity={timeline.daily_activity || []} />
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Search Query Timeline
                  </h3>
                  <span className="text-sm text-gray-500">
                    {queryMeta.total || 0} queries
                  </span>
                </div>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {(timeline.search_queries || []).length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No search queries in this period
                    </div>
                  ) : (
                    timeline.search_queries.map((query) => (
                      <div key={query.search_id} className="p-4">
                        <p className="text-sm text-gray-500">
                          {formatDateTime(query.created_at)}
                        </p>
                        <p className="mt-1 font-medium text-gray-900 whitespace-pre-wrap">
                          {query.query}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                          {query.search_mode && (
                            <span className="px-2 py-1 bg-gray-100 rounded">
                              {query.search_mode}
                            </span>
                          )}
                          <span className="px-2 py-1 bg-gray-100 rounded">
                            Papers: {query.papers_found || 0}
                          </span>
                          <span className="px-2 py-1 bg-gray-100 rounded">
                            Credits: {query.charged_credits || 0}
                          </span>
                        </div>
                        <QueryEngagement engagement={query.engagement} />
                      </div>
                    ))
                  )}
                </div>

                {queryMeta.total_pages > 1 && (
                  <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                      onClick={() => onPageChange(queryPage - 1)}
                      disabled={!queryMeta.has_prev}
                      className="p-2 border rounded-md disabled:bg-gray-100 disabled:text-gray-400"
                      aria-label="Previous query page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {queryPage} of {queryMeta.total_pages}
                    </span>
                    <button
                      onClick={() => onPageChange(queryPage + 1)}
                      disabled={!queryMeta.has_next}
                      className="p-2 border rounded-md disabled:bg-gray-100 disabled:text-gray-400"
                      aria-label="Next query page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function QueryEngagement({ engagement }) {
  const counts = engagement || {};
  const papers = Array.isArray(counts.papers) ? counts.papers : [];
  const hasEngagement = [
    counts.papers_seen,
    counts.title_clicks,
    counts.full_text_clicks,
    counts.bookmark_adds,
    counts.csv_exports,
    counts.history_opens,
  ].some((value) => Number(value || 0) > 0);

  if (!hasEngagement) {
    return (
      <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-500">
        No result engagement events recorded for this query yet.
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-sky-100 bg-sky-50/60 p-3">
      <div className="grid grid-cols-2 gap-2 text-xs text-sky-900 md:grid-cols-6">
        <EngagementStat label="Seen" value={counts.papers_seen || 0} />
        <EngagementStat label="Title clicks" value={counts.title_clicks || 0} />
        <EngagementStat label="Full text" value={counts.full_text_clicks || 0} />
        <EngagementStat label="Bookmarks" value={counts.bookmark_adds || 0} />
        <EngagementStat label="CSV" value={counts.csv_exports || 0} />
        <EngagementStat label="History" value={counts.history_opens || 0} />
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs text-sky-800">
        <span className="inline-flex items-center gap-1">
          <MetricLabel label="Title CTR" tone="sky" small />
          <span>{formatPercent(counts.title_ctr)}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <MetricLabel label="Full-text CTR" tone="sky" small />
          <span>{formatPercent(counts.full_text_ctr)}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <MetricLabel label="Bookmark rate" tone="sky" small />
          <span>{formatPercent(counts.bookmark_rate)}</span>
        </span>
        {counts.last_history_open_at && (
          <span className="inline-flex items-center gap-1">
            <MetricLabel label="Last history open" tone="sky" small />
            <span>{formatDateTime(counts.last_history_open_at)}</span>
          </span>
        )}
        {Number(counts.history_opens || 0) > 0 && (
          <span className="inline-flex items-center gap-1">
            <MetricLabel label="Sidebar opens" tone="sky" small />
            <span>{counts.sidebar_history_opens || 0}</span>
          </span>
        )}
        {Number(counts.history_opens || 0) > 0 && (
          <span className="inline-flex items-center gap-1">
            <MetricLabel label="Detail opens" tone="sky" small />
            <span>{counts.detail_history_opens || 0}</span>
          </span>
        )}
      </div>
      {papers.length > 0 && (
        <div className="mt-3 overflow-hidden rounded border border-sky-100 bg-white">
          <div className="grid grid-cols-[56px_minmax(0,1fr)_96px_96px_96px_110px] gap-2 border-b border-sky-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <MetricLabel label="Rank" small />
            <MetricLabel label="Paper" small />
            <MetricLabel label="Seen" small />
            <MetricLabel label="Title" small />
            <MetricLabel label="Full text" small />
            <MetricLabel label="Bookmark" small />
          </div>
          {papers.slice(0, 8).map((paper, index) => (
            <div
              key={`${paper.pmid || paper.title || "paper"}-${index}`}
              className="grid grid-cols-[56px_minmax(0,1fr)_96px_96px_96px_110px] gap-2 border-b border-gray-100 px-3 py-2 text-xs last:border-b-0"
            >
              <span className="text-gray-500">
                {paper.rank ? `#${paper.rank}` : "-"}
              </span>
              <span className="min-w-0 truncate text-gray-900" title={paper.title || paper.pmid}>
                {paper.title || paper.pmid || "Untitled paper"}
              </span>
              <span>{paper.seen ? "Yes" : "No"}</span>
              <span>{paper.title_clicks || 0}</span>
              <span>{paper.full_text_clicks || 0}</span>
              <span>{paper.bookmarked ? "Yes" : "No"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EngagementStat({ label, value }) {
  return (
    <div className="rounded bg-white px-2 py-2">
      <MetricLabel label={label} tone="sky" small />
      <p className="mt-1 font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function DrawerMetric({ label, value }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <MetricLabel label={label} tone="gray" />
      <p className="mt-2 text-xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function MetricLabel({ label, tone = "gray", small = false }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltip = METRIC_TOOLTIPS[label];
  const textClass =
    tone === "sky"
      ? "text-sky-500"
      : "text-gray-500";

  return (
    <div
      className="relative inline-flex items-center gap-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <p
        className={`${small ? "text-[11px]" : "text-xs"} uppercase tracking-wide ${textClass}`}
      >
        {label}
      </p>
      {tooltip && (
        <>
          <button
            type="button"
            className={`rounded text-gray-400 hover:text-gray-600 ${small ? "p-0.5" : "p-0.5"}`}
            aria-label={`${label} 说明`}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
          >
            <Info className={small ? "h-3 w-3" : "h-3.5 w-3.5"} />
          </button>
          {showTooltip && (
            <div className="absolute left-0 top-full z-20 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-3 text-left text-xs normal-case tracking-normal text-gray-700 shadow-lg">
              {tooltip}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatPercent(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "0%";
  return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`;
}

function resolveDownloadFilename(headers, fallback) {
  const disposition = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

function downloadBlob(data, filename, contentType = "application/zip") {
  const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default UserActivityTimeline;
